/**
 * Player profile wired to GET /api/players/:id/stats.
 *
 * Format filter reloads from API. Export button generates CSV client-side.
 * AI Insights section shows locked placeholder (P2).
 */
import { useEffect, useState } from "react";
import type { AuthUser, PlayerId, PlayerStatsResponse } from "@sportsos/engine";
import { api } from "../api.ts";
import { PlayStyle } from "./PlayStyle.tsx";
import { avatarColor, initials } from "./shared.ts";

const FORMATS = ["All", "T20", "ODI-40", "ODI-50", "Box Cricket"] as const;
type Format = (typeof FORMATS)[number];

interface Props {
  /** The signed-in account. Absent when this screen is showing somebody else. */
  readonly user?: AuthUser;
  /**
   * Show this player instead of the signed-in account.
   *
   * Tapping a squad member is the only way to reach another person's history, and without
   * it "which teams is he in?" had no answer anywhere in the app — the data was returned by
   * the API and had nowhere to land.
   */
  readonly viewing?: { readonly playerId: PlayerId; readonly displayName: string } | null;
  /** Shown as a back arrow when this screen was opened from somewhere. */
  readonly onBack?: (() => void) | null;
  /**
   * The account's playing identity, from `me`. Null when it has none.
   *
   * This screen used to read stats with `user.id`, which is a `UserId` handed to a query
   * that selects from `players` — so it found nothing, 404'd, and rendered a blank profile
   * for everybody. An account and a player are separate rows (PRD ID-3).
   */
  readonly playerId: PlayerId | null;
}

/*
 * DERIVED_STATS_ONLY — why this screen shows so few numbers.
 *
 * Only matches, runs, balls, fours and sixes are really derived. The rest are literal zeros
 * in the query: `packages/db/src/players.ts` selects `0` for innings, not-outs, high score,
 * fifties and hundreds, and the return block hardcodes every bowling and fielding figure
 * plus an empty `recentInnings`. Rendering those is worse than omitting them — a bowler
 * reads "0 wickets" as *his* zero rather than an unbuilt feature, and reports it as lost
 * data. They come back when the numbers are real.
 */

/** Extract up to two initials from a display name. */
function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  const first = parts[0];
  const last = parts[parts.length - 1];
  if (!first) return "?";
  if (parts.length === 1 || !last) return first.charAt(0).toUpperCase();
  return (first.charAt(0) + last.charAt(0)).toUpperCase();
}

export function Profile({ user, playerId, viewing = null, onBack = null }: Props): React.ReactElement {
  const subjectId = viewing?.playerId ?? playerId;
  const subjectName = viewing?.displayName ?? user?.displayName ?? "Player";
  const [format, setFormat] = useState<Format>("All");
  const [stats, setStats] = useState<PlayerStatsResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    setLoading(true);
    void (async () => {
      try {
        // `playerId` is read once when the app boots. Someone added to a squad while already
        // signed in was not linked to a player row at that moment, so a stale null would say
        // "no matches played yet" over a team they had just joined. Asking again on open is
        // what turns that into the truth without making them reload.
        const id = subjectId ?? (viewing === null ? (await api.meForDisplay())?.playerId ?? null : null);
        if (id === null) {
          if (live) { setStats(null); setLoading(false); }
          return;
        }
        const formatParam = format === "All" ? undefined : format;
        const data = await api.getPlayerStats(id, formatParam);
        if (live) setStats(data);
      } catch {
        if (live) setStats(null);
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => { live = false; };
  }, [subjectId, viewing, format]);

  const exportCsv = (): void => {
    if (!stats) return;
    const rows = [
      ["Stat", "Value"],
      // Same rule as the screen: export what is derived, not the placeholder zeros. A CSV
      // is worse than a screen for this — it outlives the caveat and gets shared as fact.
      ["Matches", String(stats.batting.matches)],
      ["Runs", String(stats.batting.runs)],
      ["Strike Rate", stats.batting.strikeRate !== null ? stats.batting.strikeRate.toFixed(2) : "-"],
      ["Teams", stats.teams.map((team) => team.name).join(" | ")],
    ];
    const csv = rows.map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${subjectName}_stats.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const val = (v: number | null, suffix?: string): string => {
    if (v === null) return "—";
    return suffix ? `${v}${suffix}` : String(v);
  };

  const hasPlayed = !loading && stats !== null && stats.batting.matches > 0;
  const teams = stats?.teams ?? [];
  const matches = stats?.matches ?? [];

  return (
    <div className="app-page">
      {/* ── App bar ────────────────────────────────────────────────────── */}
      <header className="ds-appbar">
        {onBack && (
          <button className="ds-iconbtn" aria-label="Back" onClick={onBack}>
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
        )}
        <span className="ds-appbar__body">
          <b className="ds-appbar__title">{viewing ? subjectName : "Profile"}</b>
          {viewing === null && user?.email && (
            <span className="ds-appbar__meta">Signed in as {user.email}</span>
          )}
        </span>
      </header>

      {/* ── Profile card ───────────────────────────────────────────────── */}
      <article className="ds-card">
        <div className="row" style={{ gap: "var(--ds-space-4)" }}>
          <span
            className="ds-avatar ds-avatar--lg ds-avatar--azure"
            style={{ width: 64, height: 64, fontSize: "var(--ds-text-xl)" }}
          >
            {getInitials(subjectName)}
          </span>
          <span style={{ flex: 1, minWidth: 0 }}>
            <b style={{
              display: "block",
              fontFamily: "var(--ds-font-display)",
              fontSize: "var(--ds-text-xl)",
              letterSpacing: "var(--ds-tracking-tight)",
            }}>
              {subjectName}
            </b>
            {/* Another player's contact details are deliberately absent — the profile API
                does not return them, and showing an account email here would leak one. */}
            {viewing === null && user?.email && <span className="ds-caption">{user.email}</span>}
          </span>
        </div>

        {loading ? (
          <div className="ds-caption" style={{ marginTop: "var(--ds-space-5)" }}>Loading stats...</div>
        ) : stats ? (
          <div className="row" style={{ marginTop: "var(--ds-space-5)", gap: "var(--ds-space-6)" }}>
            <span>
              <span className="ds-stat-value">{stats.batting.matches}</span>
              <span className="ds-stat-label" style={{ display: "block" }}>Matches</span>
            </span>
            <span>
              <span className="ds-stat-value">{stats.batting.runs}</span>
              <span className="ds-stat-label" style={{ display: "block" }}>Runs</span>
            </span>
            <span>
              <span className="ds-stat-value">
                {stats.batting.strikeRate !== null ? stats.batting.strikeRate.toFixed(1) : "—"}
              </span>
              <span className="ds-stat-label" style={{ display: "block" }}>Strike rate</span>
            </span>
          </div>
        ) : null}
      </article>

      {/* ── How they play ──────────────────────────────────────────────── */}
      {/* Outside the `hasPlayed` branch, for the same reason Teams is: someone in a squad who has
          never batted can still be known to bat left-handed (ADR-0023). */}
      {!loading && stats !== null ? (
        <PlayStyle
          playerId={String(stats.playerId)}
          style={stats.style}
          onSaved={(style) => setStats((current) => (current === null ? current : { ...current, style }))}
        />
      ) : null}

      {/* ── Format filter ──────────────────────────────────────────────── */}
      {hasPlayed && (
        <div style={{ marginTop: "var(--ds-space-5)" }}>
          <div className="ds-segmented" role="group" aria-label="Filter by format">
            {FORMATS.map((option) => (
              <button
                key={option}
                className="ds-segmented__item"
                aria-selected={format === option}
                onClick={() => setFormat(option)}
              >
                {option}
              </button>
            ))}
          </div>
        </div>
      )}

      {!hasPlayed ? (
        /* ── Empty state ─────────────────────────────────────────────── */
        <div className="ds-card" style={{ marginTop: "var(--ds-space-5)", textAlign: "center", padding: "var(--ds-space-8) var(--ds-space-5)" }}>
          <svg viewBox="0 0 48 48" width="48" height="48" fill="none" stroke="var(--ds-text-muted)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ margin: "0 auto var(--ds-space-4)", display: "block", opacity: 0.5 }}>
            <circle cx="24" cy="16" r="6" />
            <path d="M12 38a12 12 0 0 1 24 0" />
            <path d="M30 24l8-8M34 16h4v4" />
          </svg>
          <div style={{ fontWeight: 700, fontSize: "var(--ds-text-lg)", marginBottom: "var(--ds-space-2)" }}>
            No matches played yet
          </div>
          <div className="ds-caption" style={{ maxWidth: "28ch", margin: "0 auto" }}>
            Score your first match and your batting, bowling and fielding stats will appear here.
          </div>
        </div>
      ) : (
        <>
          {/* ── Batting ──────────────────────────────────────────────── */}
          <div className="sechead">
            <h2 className="ds-heading">Batting</h2>
          </div>
          <div className="ds-card">
            <div className="row" style={{ gap: "var(--ds-space-6)", flexWrap: "wrap" }}>
              <StatCell label="Matches" value={val(stats?.batting.matches ?? null)} />
              <StatCell label="Runs" value={val(stats?.batting.runs ?? null)} />
              <StatCell label="SR" value={val(stats?.batting.strikeRate ?? null)} />
              <StatCell label="Boundary %" value={val(stats?.batting.boundaryPct ?? null, "%")} />
              {/* High score, average and the fifty/hundred counts are omitted while the
                  query returns literal zeros for them — see DERIVED_STATS_ONLY. */}
            </div>
          </div>

          {/* Bowling, fielding and recent form are omitted entirely while every one of
              their numbers is a hardcoded zero in the query. See DERIVED_STATS_ONLY. */}
        </>
      )}

      {/* ── Teams ──────────────────────────────────────────────────────── */}
      {/* Outside the `hasPlayed` branch on purpose: being in a squad is exactly the state
          someone is in *before* their first match, and it was invisible until now. */}
      <div className="sechead">
        <h2 className="ds-heading">Teams</h2>
        {teams.length > 0 && <span className="ds-caption">{teams.length}</span>}
      </div>
      {teams.length > 0 ? (
        <div className="ds-list">
          {teams.map((team) => (
            <div className="ds-list-item" key={team.teamId}>
              <span className={`ds-avatar ds-avatar--sm ds-avatar--${avatarColor(team.name)}`}>
                {initials(team.name)}
              </span>
              <span className="ds-list-item__body">
                <b className="ds-list-item__title">{team.name}</b>
                <span className="ds-list-item__meta">
                  {team.squadSize} player{team.squadSize !== 1 ? "s" : ""}
                </span>
              </span>
            </div>
          ))}
        </div>
      ) : (
        <div className="ds-card" style={{ textAlign: "center" }}>
          <span className="ds-caption">Not in any team yet.</span>
        </div>
      )}

      {/* ── Matches ────────────────────────────────────────────────────── */}
      <div className="sechead">
        <h2 className="ds-heading">Matches</h2>
        {matches.length > 0 && <span className="ds-caption">{matches.length}</span>}
      </div>
      {matches.length > 0 ? (
        <div className="ds-list">
          {matches.map((match) => (
            <div className="ds-list-item" key={match.matchId}>
              <span className={`ds-avatar ds-avatar--sm ds-avatar--${avatarColor(match.oppositionName)}`}>
                {initials(match.oppositionName)}
              </span>
              <span className="ds-list-item__body">
                <b className="ds-list-item__title">
                  {match.teamName} vs {match.oppositionName}
                </b>
                <span className="ds-list-item__meta">
                  {match.resultText ?? (match.status === "live" ? "In progress" : "No result yet")}
                </span>
              </span>
            </div>
          ))}
        </div>
      ) : (
        <div className="ds-card" style={{ textAlign: "center" }}>
          <span className="ds-caption">No matches yet.</span>
        </div>
      )}

      {/* ── AI Insights placeholder (P2) ───────────────────────────────── */}
      <div style={{ marginTop: "var(--ds-space-5)" }}>
        <div className="ds-card" style={{ borderLeft: "3px solid var(--ds-accent)" }}>
          <b style={{ display: "block", marginBottom: "var(--ds-space-1)" }}>AI Insights</b>
          <span className="ds-caption">
            Strengths, weaknesses, form trends — coming soon with CricAI.co Intelligence.
          </span>
        </div>
      </div>
    </div>
  );
}

function StatCell({ label, value }: { readonly label: string; readonly value: string }): React.ReactElement {
  return (
    <span>
      <span
        className="ds-stat-value"
        aria-label={value === "—" ? `${label}: not available yet` : `${label}: ${value}`}
      >
        {value}
      </span>
      <span className="ds-stat-label" style={{ display: "block" }}>{label}</span>
    </span>
  );
}
